import React, { createContext, useContext, useReducer, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { fleetService } from '../services/api';
import { useAuth } from './AuthContext';

// ─── Gestion de flotte — véhicules, entretiens, documents, signalements ────
// Vraie base Postgres (voir server/fleet.js/schema.sql), plus en localStorage
// : avant, tout ce state vivait dans un useReducer persisté côté navigateur
// (un onglet resté ouvert = une "vérité" séparée), jamais vraiment partagé
// entre l'admin et le chauffeur ni entre deux appareils. L'interface publique
// de ce contexte (vehicles/maintenanceTypes/.../dispatch({type,...})) reste
// identique pour ne rien changer dans FleetView.jsx, DocumentsView.jsx,
// ReportsView.jsx, VehiclesGalleryView.jsx, AdminDashboard.jsx et
// DriverDashboard.jsx : dispatch met à jour l'état local (optimiste) ET
// répercute réellement l'action en base (voir syncToServer plus bas).

const initState = {
  vehicles: [],
  maintenanceTypes: [],
  maintenanceRecords: [],
  documentTypes: [],
  documents: [],
  driverReports: [],
  geofence: null,
  drivers: [],
};

function reducer(state, action) {
  switch (action.type) {
    // Remplace tout l'état par ce qui vient réellement du serveur (montage,
    // ou reconnexion) — jamais un état par défaut inventé localement.
    case 'HYDRATE':
      return { ...state, ...action.state };

    case 'ADD_VEHICLE':
      return { ...state, vehicles: [...state.vehicles, action.vehicle] };
    case 'UPDATE_VEHICLE':
      return { ...state, vehicles: state.vehicles.map(v => v.id === action.id ? { ...v, ...action.updates } : v) };
    case 'DELETE_VEHICLE':
      return {
        ...state,
        vehicles: state.vehicles.filter(v => v.id !== action.id),
        maintenanceRecords: state.maintenanceRecords.filter(r => r.vehicleId !== action.id),
        documents: state.documents.filter(d => d.vehicleId !== action.id),
        driverReports: state.driverReports.filter(r => r.vehicleId !== action.id),
      };
    case 'UPDATE_MILEAGE':
      return { ...state, vehicles: state.vehicles.map(v => v.id === action.id ? { ...v, mileage: Math.max(v.mileage, action.km) } : v) };

    case 'ADD_MAINTENANCE_TYPE':
      return { ...state, maintenanceTypes: [...state.maintenanceTypes, action.mtype] };
    case 'UPDATE_MAINTENANCE_TYPE':
      return { ...state, maintenanceTypes: state.maintenanceTypes.map(t => t.id === action.id ? { ...t, ...action.updates } : t) };
    case 'DELETE_MAINTENANCE_TYPE':
      return { ...state, maintenanceTypes: state.maintenanceTypes.filter(t => t.id !== action.id) };

    case 'ADD_MAINTENANCE_RECORD': {
      const vehicles = state.vehicles.map(v => v.id === action.record.vehicleId ? { ...v, mileage: Math.max(v.mileage, action.record.km) } : v);
      return { ...state, vehicles, maintenanceRecords: [action.record, ...state.maintenanceRecords] };
    }
    case 'DELETE_MAINTENANCE_RECORD':
      return { ...state, maintenanceRecords: state.maintenanceRecords.filter(r => r.id !== action.id) };
    case 'UPDATE_MAINTENANCE_RECORD': {
      const existing = state.maintenanceRecords.find(r => r.id === action.id);
      if (!existing) return state;
      const merged = { ...existing, ...action.updates };
      const vehicles = state.vehicles.map(v => v.id === merged.vehicleId ? { ...v, mileage: Math.max(v.mileage, merged.km) } : v);
      return { ...state, vehicles, maintenanceRecords: state.maintenanceRecords.map(r => r.id === action.id ? merged : r) };
    }

    case 'ADD_DOCUMENT':
      return { ...state, documents: [action.document, ...state.documents] };
    case 'DELETE_DOCUMENT':
      return { ...state, documents: state.documents.filter(d => d.id !== action.id) };
    case 'UPDATE_DOCUMENT_TYPE':
      return { ...state, documentTypes: state.documentTypes.map(t => t.id === action.id ? { ...t, ...action.updates } : t) };

    case 'REPORT_ISSUE':
      return { ...state, driverReports: [action.report, ...state.driverReports] };
    case 'RESOLVE_ISSUE':
      return { ...state, driverReports: state.driverReports.map(r => r.id === action.id ? { ...r, status: 'traité' } : r) };

    case 'SET_GEOFENCE':
      return { ...state, geofence: action.geofence };

    default:
      return state;
  }
}

// Répercute réellement chaque action en base — un seul point de vérité entre
// le type d'action du reducer ci-dessus et la vraie route serveur. En cas
// d'échec (réseau, permission), l'état local reste optimiste jusqu'à la
// prochaine hydratation : on prévient au moins via un toast plutôt que de
// laisser croire silencieusement que c'est enregistré.
async function syncToServer(action) {
  try {
    switch (action.type) {
      case 'ADD_VEHICLE': await fleetService.addVehicle(action.vehicle); break;
      case 'UPDATE_VEHICLE': await fleetService.updateVehicle(action.id, action.updates); break;
      case 'DELETE_VEHICLE': await fleetService.deleteVehicle(action.id); break;
      case 'UPDATE_MILEAGE': await fleetService.updateMileage(action.km); break;
      case 'ADD_MAINTENANCE_TYPE': await fleetService.addMaintenanceType(action.mtype); break;
      case 'UPDATE_MAINTENANCE_TYPE': await fleetService.updateMaintenanceType(action.id, action.updates); break;
      case 'DELETE_MAINTENANCE_TYPE': await fleetService.deleteMaintenanceType(action.id); break;
      case 'ADD_MAINTENANCE_RECORD': await fleetService.addMaintenanceRecord(action.record); break;
      case 'UPDATE_MAINTENANCE_RECORD': await fleetService.updateMaintenanceRecord(action.id, action.updates); break;
      case 'DELETE_MAINTENANCE_RECORD': await fleetService.deleteMaintenanceRecord(action.id); break;
      case 'ADD_DOCUMENT': await fleetService.addDocument(action.document); break;
      case 'DELETE_DOCUMENT': await fleetService.deleteDocument(action.id); break;
      case 'UPDATE_DOCUMENT_TYPE': await fleetService.updateDocumentType(action.id, action.updates); break;
      case 'REPORT_ISSUE': await fleetService.reportIssue(action.report); break;
      case 'RESOLVE_ISSUE': await fleetService.resolveIssue(action.id); break;
      case 'SET_GEOFENCE': await fleetService.setGeofence(action.geofence); break;
      default: break; // HYDRATE n'a rien à répercuter, c'est déjà la vérité serveur
    }
  } catch (err) {
    console.error('Échec de synchronisation flotte', action.type, err);
    toast.error(err.response?.data?.error || "Échec de l'enregistrement — réessayez");
  }
}

const FleetCtx = createContext(null);

export function FleetProvider({ children }) {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(reducer, initState);
  // Évite un double chargement (React 18 StrictMode monte les effets deux
  // fois en dev) et recharge proprement si l'utilisateur change (déconnexion
  // puis reconnexion sous un autre compte dans le même onglet).
  const lastLoadedUserId = useRef(null);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'driver')) {
      lastLoadedUserId.current = null;
      return;
    }
    if (lastLoadedUserId.current === user.id) return;
    lastLoadedUserId.current = user.id;
    (async () => {
      try {
        const res = user.role === 'admin' ? await fleetService.getState() : await fleetService.getMine();
        dispatch({ type: 'HYDRATE', state: { ...initState, ...res.data } });
      } catch (err) {
        console.error('Échec du chargement de la flotte', err);
      }
    })();
  }, [user]);

  const syncDispatch = (action) => {
    dispatch(action);
    syncToServer(action);
  };

  // driverName résolu ici (jamais stocké) : si le chauffeur est renommé côté
  // compte ou n'existe plus, tous les affichages du nom se mettent à jour
  // ensemble plutôt que de garder chacun leur propre copie périmée.
  const vehicles = useMemo(() => state.vehicles.map(v => ({
    ...v,
    driverName: v.driverId ? (state.drivers.find(d => d.id === v.driverId)?.name || 'Chauffeur introuvable') : null,
  })), [state.vehicles, state.drivers]);

  return <FleetCtx.Provider value={{ ...state, vehicles, dispatch: syncDispatch }}>{children}</FleetCtx.Provider>;
}

export const useFleet = () => {
  const ctx = useContext(FleetCtx);
  if (!ctx) throw new Error('useFleet must be used within a FleetProvider');
  return ctx;
};
